import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { useDates } from "../../../hooks/useDates";
import GenericPromises from "../../../api/GenericPromises";
import { useFormatNumber } from "../../../hooks/useFormatNumber";
import useSnackBar from "../../../components/Commons/SnackBar/useSnackBar";
import { useCallback, useEffect, useState } from "react";
import { GridColDef, GridRowSelectionModel, GridRowsProp, GridTreeNodeWithRender, GridValueGetterParams } from "@mui/x-data-grid";
import { Menuitem } from "../../../interfaces/Security/menu";
import { usePermissions } from "../../../hooks/usePermissions";
import { SalesPreorder } from "../../../interfaces/Sales/Preorders/SalesPreorders";
import { Spinner } from "../../../components/Commons/Spinner/Spinner";
import { Header } from "../../../components/Header";
import { ButtonStyled, PrimaryButton } from "../../../theme/buttons";
import DataTable from "../../../components/Tables/GridTableMaterialUI/DataTable";
import React from "react";
import EmailIcon from '@mui/icons-material/Email';
import { AuthContext } from "../../../context/AuthContext";
import { UserCompanies } from "../../../interfaces/Security/userCompanies";
import { useBase64 } from "../../../hooks/useBase64";
import { Button, ListItemButton, Popover } from "@mui/material";
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClipboardCheck } from "@fortawesome/free-solid-svg-icons";
import { DialogProcessOrder } from "./components/DialogProcessOrder";
import { DialogSendEmails } from "./helpers/dialogSendEmails";

export const TableSalesPreorders = () => {
  const [t] = useTranslation("global");
  const navigate = useNavigate();
  const location = useLocation();
  const { TimeConverter } = useDates();
  const { GenericGetResource, GenericGetResourceGeneric } = GenericPromises();
  const { setFormatNumberFromTable } = useFormatNumber();
  const { showSnackBar, SnackbarComponent } = useSnackBar();
  const { utf8ToBase64 } = useBase64();
  const { GetResourceByUrl } = usePermissions();
  const { user } = React.useContext(AuthContext);
  const [isLoadingDataTable, setIsLoadingDataTable] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [unauthorized, setUnauthorized] = useState(true);
  const [hiddenHeaderCheckBox, setHiddenHeaderCheckBox] = useState(true);
  const [openDialogProcessOrder, setOpenDialogProcessOrder] = useState(false);
  const [salesPreordersData, setSalesPreordersData] = useState<GridRowsProp>([]);
  const [salesPreordersDataAll, setSalesPreordersDataAll] = useState<GridRowsProp>([]);
  const [selectedRows, setSelectedRows] = useState<GridRowsProp>([]);
  const [resourceScreen, setResourceScreen] = useState<Menuitem>();
  const [resourceScreenProcessOrder, setResourceScreenProcessOrder] = useState<Menuitem>();
  const [messageSnack, setMessageSnack] = useState("");
  const [myPreferences, setMyPreferences] = useState({});
  const [idCustomerDefault, setIdCustomerDefault] = useState<number>();
  const [anchorEl1, setAnchorEl1] = useState<HTMLButtonElement | null>(null);
  const open1 = Boolean(anchorEl1);
  const id1 = open1 ? "simple-popover" : undefined;
  const [openEmail, setOpenEmail] = useState(false);
  const [resourceSendEmail, setResourceSendEmail] = useState<Menuitem>();
  const [dataEmails, setDataEmails] = useState<any>([]);
  const [columns, setColumns] = useState<GridColDef<SalesPreorder>[]>([
    { field: 'sales_preorder_name', headerName: t("salespreorders.fields.sales_preorder_name"), headerClassName: 'header-grid-table', flex: 1 },
    { field: 'customer_business_name', headerName: t("salespreorders.fields.customer_business_name"), headerClassName: 'header-grid-table', flex: 1 },
    {
      field: 'sales_preorder_date', headerName: t("salespreorders.fields.sales_preorder_date"), headerClassName: 'header-grid-table', type: "date", flex: 1,
      valueGetter(params) {
        return TimeConverter(params.value);
      },
    },
    { field: 'purchase_order', headerName: t("salespreorders.fields.purchase_order"), headerClassName: 'header-grid-table', flex: 1 },
    { field: 'currency_code', headerName: t("currencies.title-view"), headerClassName: 'header-grid-table', flex: 1 },
    {
      field: 'total_amount', headerName: t("salespreorders.fields.total_amount"), headerClassName: 'header-grid-table', flex: 1, type: 'number', align: "left", headerAlign: "left",
      valueGetter(params) {
        return setFormatNumberFromTable(params.value ?? 0);
      },
    },
    {
      field: 'total_company_currency_amount', headerName: t("companies.configuration.fields.total_company_currency_amount"), headerClassName: 'header-grid-table', flex: 1,
      valueGetter(params) {
        return setFormatNumberFromTable(params.value ?? 0);
      },
    },
    {
      field: 'creation_date', headerName: t("generic.creation_date"), headerClassName: 'header-grid-table', type: "date", flex: 1,
      valueGetter(params) {
        return TimeConverter(params.value);
      },
    },
    { field: 'last_update_user', headerName: t("generic.last_update_user"), headerClassName: 'header-grid-table', flex: 1 },
    {
      field: 'last_update_date', headerName: t("generic.last_update_date"), headerClassName: 'header-grid-table', type: "date", flex: 1,
      valueGetter(params) {
        return TimeConverter(params.value);
      },
    },
  ]);

  const handleClose1 = () => {
    setAnchorEl1(null);
  };

  const handleClick1 = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl1(event.currentTarget);
  };

  const methodUsed = useCallback(() => {
    if (!location.state?.method) return;

    switch (location.state.method) {
      case "add":
        {
          showSnackBar(t("generic.snackbar.add"), "success");
          break;
        }
      case "delete":
        {
          showSnackBar(t("generic.snackbar.delete"), "success");
          break;
        }
      case "error":
        {
          showSnackBar(location.state.error, "error");
          break;
        }
    }

    navigate(".", { state: { ...location.state, method: undefined }, replace: true });
  }, [location.state, t]);

  const loadPreferences = async () => {
    let myPreferences = await localStorage.getItem("grid-salespreorders");
    if (myPreferences != null) {
      setMyPreferences(JSON.parse(myPreferences));
    }
  }

  const handleAddButton = async () => {
    navigate('add', {
      state: {
        ...location.state,
        customer_id: idCustomerDefault,
      },
    });
  }

  const loadColumnsOrder = async () => {
    let myOrderColumns = await localStorage.getItem("grid-salespreorders-columns");
    if (myOrderColumns != null) {
      let myJson = JSON.parse(myOrderColumns);
      for (let index = 0; index < myJson.length; index++) {
        const element = myJson[index];
        if (element['type'] === 'date') {
          if (element.field === 'sales_preorder_date' || element.field === 'date_mail_send') {
            element.headerName = t(`salespreorders.fields.${element.field}`);
            element.valueGetter = function (params: GridValueGetterParams<SalesPreorder, any, GridTreeNodeWithRender>) {
              return TimeConverter(params.value);
            };
          }
          else {
            element.headerName = t(`generic.${element.field}`);
          }
        }
        else {
          if ((element.field === 'total_amount') || (element.field === 'total_company_currency_amount')) {
            element.valueGetter = function (params: GridValueGetterParams<SalesPreorder, any, GridTreeNodeWithRender>) {
              return setFormatNumberFromTable(params.value ?? 0);
            }
          }
          else if (element.field === 'currency_code') { element.headerName = t("currencies.title-view"); }
          else if (element.field === 'total_company_currency_amount') {
            element.headerName = t("companies.configuration.fields.total_company_currency_amount");
            element.valueGetter = function (params: GridValueGetterParams<SalesPreorder, any, GridTreeNodeWithRender>) {
              return setFormatNumberFromTable(params.value ?? 0);
            }
          }
          else if (element.field === 'last_update_user') { element.headerName = t(`generic.${element.field}`); }
          else if (element.field === 'customer_business_name') { element.headerName = t(`salespreorders.fields.customer_business_name`); }
          else {
            element.headerName = t(`salespreorders.fields.${element.field}`);
          }
        }
      }
      setColumns(myJson);
    }
  }

  const onRowSelection = (rowsIds: GridRowSelectionModel) => {
    if (rowsIds.length === 1) {
      const myRow = salesPreordersData.find(item => item.sales_preorder_id === rowsIds[0]);
      if (hiddenHeaderCheckBox) {
        setSalesPreordersDataAll([...salesPreordersData]);
      }
      setSalesPreordersData(salesPreordersData.filter(item =>
        item.customer_id === myRow?.customer_id && item.customer_business_name === myRow?.customer_business_name
      ));
      setHiddenHeaderCheckBox(false);
      setSelectedRows([{ ...myRow }]);
    } else if (rowsIds.length === 0) {
      setSalesPreordersData([...salesPreordersDataAll]);
      setHiddenHeaderCheckBox(true);
      setSelectedRows([]);
    } else if (rowsIds.length > 1) {
      const selectedData = salesPreordersData.filter(item =>
        rowsIds.includes(item.sales_preorder_id)
      );
      setSelectedRows((prev) => selectedData);
    }
  };

  const onSendEmail = () => {
    let dataEmails: any = [];
    setDataEmails(selectedRows.forEach((element: any) => {
      let data = {
        data: element,
        status: (element.customer_email) ? "Waiting" : "Error",
        status_description:
          (element.customer_email) ? "generic.send_email.waiting" :
            "generic.send_email.no_email"
      }
      dataEmails.push(data)
    }));
    setDataEmails(dataEmails);
    setOpenEmail(true);
  }

  useEffect(() => {
    const loadData = async () => {
    GenericGetResourceGeneric("/companies", "/gcompanies").then((response) => {
      document.title = `${response.data.data[0].comercial_name} - ${t("salespreorders.title")}`;
    })

    if (location.state !== null) {
      if (location.state.method) {
        methodUsed();
      }
      else if (location.state.row && location.state.row.sales_preorder_id) {
        navigate("/salespreorders/view", { state: { row: location.state.row } })
      }
    }

    GetResourceByUrl(`/salespreorders`)
      .then((responsePermission) => {
        setResourceScreen((prev) => responsePermission);
        loadColumnsOrder();
        loadPreferences();
        setDataLoaded(true);
      })
      .catch((error) => {
        showSnackBar(error.message, "error");
        setMessageSnack(error.message);
        setUnauthorized(false);
      });

    const sendEmailResponse = await GetResourceByUrl(`/salespreorders/sendemail`);
    setResourceSendEmail(sendEmailResponse);

    GetResourceByUrl(`/salespreorders/processorder`)
      .then((responsePermission) => {
        setResourceScreenProcessOrder((prev) => responsePermission);
      });
    }

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (resourceScreen?.read) {
      GenericGetResourceGeneric(`/usercompanies/byuserid/${user?.user_id}`, "/gusercompanies")
        .then((response) => {
          let myCustomerDefault = response.data.data.find((element: UserCompanies) => element.schema_name === user?.schema)
          let myRoute = `/salespreorders`;
          if (myCustomerDefault && myCustomerDefault.customer_default_id) {
            myRoute = `/salespreorders?filter=${utf8ToBase64(`customer_id=${myCustomerDefault.customer_default_id}`)}`;
            setIdCustomerDefault(myCustomerDefault.customer_default_id);
          }
          GenericGetResource(myRoute)
            .then(async (responseSalesPreorders) => {
              await setSalesPreordersData(responseSalesPreorders.data.data);
              setIsLoadingDataTable(true);
            }
            ).catch((error) => {
              showSnackBar(error.message, "error");
              setMessageSnack(error.message);
              setUnauthorized(false);
            });
        })
    }
  }, [resourceScreen]);

  useEffect(() => { }, [selectedRows]);

  return (
    <>
      {!unauthorized && <div className='screen-container d-flex flex-column justify-content-center align-items-center'> <img alt='ERROR' style={{ height: "20rem", width: "20rem" }} src={require("../../../assets/img/error.webp")} /> <h2>{messageSnack}</h2> </div>}
      {unauthorized && !dataLoaded && <Spinner />}
      {dataLoaded && resourceScreen?.read && <div className='screen-container'>
        <Header
          title={t("salespreorders.title")}
          child={
            <div className='d-flex flex-row-reverse my-1'>
              <Button
                aria-describedby={id1}
                onClick={handleClick1}
                variant='outlined'
                size="small"
                sx={{ marginBlock: .5 }}
              >
                <MoreHorizIcon />
              </Button>
              <Popover
                id={id1}
                open={open1}
                anchorEl={anchorEl1}
                onClose={handleClose1}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "left",
                }}
                sx={{ minWidth: 200 }}
              >
                <ListItemButton
                  onClick={() => {
                    if (selectedRows.length === 0) {
                      showSnackBar(t("salespreorders.info.select-rows"), "warning");
                    }
                    else if (selectedRows.some(row => row.has_sales_order === true)) {
                      showSnackBar(t("salespreorders.info.has-sales-order"), "warning");
                    }
                    else {
                      setOpenDialogProcessOrder(true);
                    }
                  }}
                  disabled={!resourceScreenProcessOrder?.create}
                  sx={{
                    p: 2,
                    display: "flex",
                    justifyContent: "space-between",
                    minWidth: 200,
                  }}
                >
                  {t("salespreorders.actions.processorder")}
                  <FontAwesomeIcon icon={faClipboardCheck} />
                </ListItemButton>
              </Popover>
              {resourceScreen?.create === true &&
                <PrimaryButton variant='outlined' className="m-1" onClick={handleAddButton}>{t("generic.buttons.add")}</PrimaryButton>
              }
              {resourceSendEmail?.create === true &&
                <ButtonStyled variant='outlined' className="m-1" sx={{ color: 'primary.main' }} disabled={selectedRows.length === 0} onClick={onSendEmail}>{t("generic.buttons.send_email")}<EmailIcon sx={{ marginLeft: 1 }} /></ButtonStyled>
              }
            </div>
          }
        />
        <div className="d-flex justify-content-center">
          {!isLoadingDataTable && <Spinner />}
          {isLoadingDataTable &&
            <DataTable
              columns={columns}
              setColumns={setColumns}
              data={salesPreordersData}
              entityId={"sales_preorder_id"}
              entity={`SalesPreorders`}
              preferences={myPreferences}
              namePreferences={"grid-salespreorders"}
              nameOrderColumns={"grid-salespreorders-columns"}
              selectRows
              hiddenHeaderCheckBox={hiddenHeaderCheckBox}
              onRowSelectionModelChange={(rowsIds) => onRowSelection(rowsIds)}
              countColumns={["total_company_currency_amount"]}
            />
          }
        </div>
        <SnackbarComponent />
        <DialogProcessOrder
          open={openDialogProcessOrder}
          setOpenDialog={setOpenDialogProcessOrder}
          customerId={selectedRows.length ? selectedRows[0].customer_id : 0}
          salesPreorderIds={selectedRows.map((item) => item.sales_preorder_id)}
        />
        <DialogSendEmails
          open={openEmail}
          setOpen={setOpenEmail}
          data={dataEmails}
          showSnackBar={showSnackBar}
        />
      </div>
      }
    </>
  )
}
